import type { Role, SessionUser } from '@atlas/api-contract';
import {
  useMutation,
  useQuery,
  useQueryClient,
  type UseMutationResult,
  type UseQueryResult,
} from '@tanstack/react-query';

import { getMe, logout } from '@/lib/api/auth';
import { isApiError, type ApiError } from '@/lib/api/errors';
import { queryKeys } from '@/lib/query/keys';

import {
  canCreateContent,
  canManageUsers,
  canModifyComment,
  canModifyOwned,
} from './permissions';

/**
 * The session, and the permission helpers bound to it.
 *
 * `null` session data means "signed out": `useSession` maps a `401` from
 * `GET /v1/auth/me` to `null` rather than an error, because a missing session
 * is the normal state of a first visit, not a failure to show the user. Every
 * other error is left to surface. The 401 is never retried — the axios layer
 * has already made its one refresh attempt (spec R7.5).
 */

/** The `SessionUser`, or `null` when signed out. */
export const useSession = (): UseQueryResult<SessionUser | null, ApiError> =>
  useQuery<SessionUser | null, ApiError>({
    queryKey: queryKeys.auth.session(),
    queryFn: async ({ signal }) => {
      try {
        return await getMe({ signal });
      } catch (error: unknown) {
        if (isApiError(error) && error.status === 401) {
          return null;
        }
        throw error;
      }
    },
    /* Signed-in state is stable for a session; no need to refetch on mount. */
    staleTime: 5 * 60_000,
    /* A 401 is an answer, not a transient failure — never retry it. */
    retry: false,
  });

/** What the UI needs about who is signed in, plus its permission predicates. */
export interface CurrentUser {
  /** The full session user, or `null` when signed out. */
  user: SessionUser | null;
  /** Convenience: the id, or `null` when signed out. */
  id: string | null;
  /** Convenience: the role, or `null` when signed out. */
  role: Role | null;
  isAuthenticated: boolean;
  isAdmin: boolean;
}

export const useCurrentUser = (): CurrentUser => {
  const session = useSession();
  const user = session.data ?? null;

  return {
    user,
    id: user?.id ?? null,
    role: user?.role ?? null,
    isAuthenticated: user !== null,
    isAdmin: user?.role === 'ADMIN',
  };
};

/** Permission predicates for the signed-in user; all default to denied. */
export interface Capabilities {
  createProject: boolean;
  createPrototype: boolean;
  manageUsers: boolean;
  /** Edit/delete a project, prototype or version owned by `ownerId`. */
  modifyOwned: (ownerId: string | null) => boolean;
  /** Upload a version to a prototype owned by `ownerId`. */
  uploadVersion: (ownerId: string | null) => boolean;
  /** Edit/delete a comment authored by `authorId`. */
  modifyComment: (authorId: string | null) => boolean;
}

/**
 * The current user's capabilities, ready to gate a control with.
 *
 * A convenience layer over {@link permissions}; the API is the real check
 * (spec R7.8). Everything is denied when signed out, which never actually
 * renders because the auth gate shows the sign-in screen instead — but it keeps
 * these predicates honest if one is ever read early.
 */
export const useCan = (): Capabilities => {
  const { role, id } = useCurrentUser();

  const deny = role === null || id === null;

  return {
    createProject: !deny && canCreateContent(role),
    createPrototype: !deny && canCreateContent(role),
    manageUsers: !deny && canManageUsers(role),
    modifyOwned: (ownerId) => !deny && canModifyOwned(role, ownerId, id),
    uploadVersion: (ownerId) => !deny && canModifyOwned(role, ownerId, id),
    modifyComment: (authorId) => !deny && canModifyComment(role, authorId, id),
  };
};

/**
 * Sign out (spec R7.5).
 *
 * Clears the whole cache and sets the session to `null`. Emptying the session
 * query is what flips the app back to the sign-in screen — the router is
 * unmounted below the gate, so there is no route to navigate.
 */
export const useLogout = (): UseMutationResult<void, ApiError, void> => {
  const queryClient = useQueryClient();

  return useMutation<void, ApiError, void>({
    mutationFn: logout,
    onSuccess: () => {
      queryClient.clear();
      queryClient.setQueryData(queryKeys.auth.session(), null);
    },
    /*
     * Even if the server call fails, the intent is unambiguous: drop the
     * session locally so the user lands on sign-in rather than a half-signed-out
     * app.
     */
    onError: () => {
      queryClient.clear();
      queryClient.setQueryData(queryKeys.auth.session(), null);
    },
  });
};
